import React, { useState, useRef, useEffect, useCallback, memo } from 'react'
import styled, { keyframes } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faComment, faTimes } from '@fortawesome/free-solid-svg-icons'
import { useClickAway, useDebounce, useKey } from 'react-use'
import { Tooltip } from 'react-tooltip'
import { FixedSizeList as ListBase } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import InfiniteLoaderBase from 'react-window-infinite-loader'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import llmChatListDefinition from '~/packages/llmChat/list/llmChatList.definition'
import { formatDateRelative } from '~/prix/types/date'
import { random } from 'mathjs'
import { useNavigate } from 'react-router-dom'
import { Chat } from './chatCard'

const InfiniteLoader = InfiniteLoaderBase as unknown as React.ComponentType<any>
const List = ListBase as unknown as React.ComponentType<any>

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 100px;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  padding-top: ${({ $isOpen }) => ($isOpen ? '0px' : '50px')};
  transition: all 0.2s ease-in-out;
  z-index: 1000;

  @media (max-width: 1150px) {
    padding-left: 0px !important;
  }
`

const ModalContainer = styled.div`
  background: white;
  border-radius: 20px;
  padding-bottom: 10px;
  box-shadow: 0px 5px 20px 3px rgba(0, 0, 0, 0.075);
  width: 45%;
  max-width: 700px;
  position: relative;
  overflow: hidden;

  @media (max-width: 990px) {
    width: 95dvw;
  }
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 400px;
`

const SearchInput = styled.input`
  padding: 20px;
  border: 0px;
  background: transparent;
  font-size: 16px;
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.mediumGrey};

  &:focus {
    outline: none;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  font-size: 20px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.darkText};
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease-in-out;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const SearchResults = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const SearchResultNoItems = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 20px;
  border-radius: 10px;
  text-align: left;
`

const SearchResultItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.lightGrey};
  cursor: pointer;
  outline: none;
  border: none;
  text-align: left;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }
`

const SearchResultItemTitle = styled.div`
  flex: 1;
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SearchResultItemDate = styled.div`
  font-size: 12px;
  display: none;
  opacity: 0;
  transition: all 0.2s;

  ${SearchResultItem}:hover & {
    display: flex;
    opacity: 1;
  }
`

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`

const SkeletonItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.lightGrey};
  position: relative;
  overflow: hidden;
  min-height: 38.39px;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    animation: ${shimmer} 1.2s linear infinite;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.lightGrey} 0%,
      white 50%,
      ${({ theme }) => theme.colors.lightGrey} 100%
    );
    background-size: 1000px 100%;
  }
`

const SkeletonIcon = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.mediumGrey};
  position: relative;
  z-index: 1;
`

const SkeletonText = styled.div`
  height: 16px;
  background: ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 4px;
  position: relative;
  z-index: 1;
`

const skeleton = [
  {
    width: random(20, 80) + '%',
  },
  {
    width: random(20, 80) + '%',
  },
  {
    width: random(20, 80) + '%',
  },
  {
    width: random(20, 80) + '%',
  },
]

interface SearchModalProps {
  isOpen: boolean
  onClose: () => void
  isArchived?: boolean
  paddingLeft?: number
}

function SearchModal({ isOpen, onClose, isArchived = false, paddingLeft = 0 }: SearchModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const overlayRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<any>(null)
  const [isLoadingSearch, setIsLoadingSearch] = useState<boolean>(false)
  const [listSearchResult, setListSearchResult] = useState<Chat[]>([])
  const hasMoreChats = useRef<boolean>(false)
  const currentSearchRef = useRef<{ abort: () => void } | null>(null)
  const navigate = useNavigate()

  const { callAction: reloadList } = useActionFunction(llmChatListDefinition, {
    initialIsLoading: false,
  })

  useClickAway(overlayRef, () => {
    if (isOpen) {
      onClose()
    }
  })

  useKey(
    'Escape',
    () => {
      if (isOpen) {
        onClose()
      }
    },
    {},
    [isOpen],
  )

  useEffect(() => {
    if (isOpen) {
      setIsLoadingSearch(true)

      setTimeout(() => {
        inputRef.current?.focus()
        listRef.current?.scrollTo(0)
      }, 100)
    }

    setSearchTerm('')
  }, [isOpen])

  const handleSearch = useCallback(async () => {
    // Cancela a chamada anterior se existir
    if (currentSearchRef.current) {
      currentSearchRef.current?.abort?.()
    }

    setIsLoadingSearch(true)
    setListSearchResult([])

    try {
      const call = reloadList({
        limit: 20,
        search: searchTerm,
        showArchived: isArchived,
      })

      // Guarda a referência da chamada atual
      currentSearchRef.current = call

      const result = await call.resultPromise
      setListSearchResult(result?.items ?? [])
      hasMoreChats.current = result?.hasMore ?? false
    } catch (error: any) {
      // Ignora erros de abort
      if (error?.name !== 'AbortError') {
        console.error('Erro ao buscar chats:', error)
      }
    } finally {
      setIsLoadingSearch(false)
      currentSearchRef.current = null
    }
  }, [reloadList, searchTerm, isArchived])

  useDebounce(
    () => {
      if (isOpen) handleSearch()
    },
    400,
    [searchTerm, isArchived, isOpen],
  )

  const handleClick = useCallback(
    (chatId: number) => {
      if (isArchived) {
        navigate(`/app/ai/archived/${chatId}`)
      } else {
        navigate(`/app/ai/${chatId}`)
      }
      onClose()
    },
    [navigate, isArchived, onClose],
  )

  // Função para verificar se um item está carregado
  const isItemLoaded = (index: number) => {
    return !hasMoreChats.current || index < listSearchResult.length
  }

  // Função para carregar mais itens
  const loadMoreItems = useCallback(
    async (startIndex: number) => {
      if (isLoadingSearch) return

      const { resultPromise } = reloadList({
        limit: 10,
        offset: startIndex,
        search: searchTerm,
        showArchived: isArchived,
      })

      const result = await resultPromise
      setListSearchResult(prev => {
        const newItems = [...prev]
        result?.items?.forEach((item, index) => {
          newItems[startIndex + index] = item
        })
        return newItems
      })
      hasMoreChats.current = result?.hasMore ?? false
    },
    [reloadList, searchTerm, isArchived, isLoadingSearch],
  )

  // Renderiza um item da lista
  const ItemRenderer = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    if (!isItemLoaded(index)) {
      return (
        <div style={{ ...style, padding: '10px 10px 0' }} key={`skeleton-${index}`}>
          <SkeletonItem>
            <SkeletonIcon />
            <SkeletonText style={{ width: skeleton[index % skeleton.length].width }} />
          </SkeletonItem>
        </div>
      )
    }

    const chat = listSearchResult[index]
    if (!chat) return null

    return (
      <div style={{ ...style, padding: '10px 10px 0' }} key={`item-${index}`}>
        <SearchResultItem onClick={() => handleClick(chat.id)}>
          <FontAwesomeIcon icon={faComment} />
          <SearchResultItemTitle>{chat?.title}</SearchResultItemTitle>
          <SearchResultItemDate>{formatDateRelative(chat?.createdAt)}</SearchResultItemDate>
        </SearchResultItem>
      </div>
    )
  }

  return (
    <>
      <ModalOverlay $isOpen={isOpen} style={{ paddingLeft: paddingLeft + 'px' }}>
        <ModalContainer ref={overlayRef}>
          <CloseButton className='llm-search-close' onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
          <ModalContent>
            <SearchInput
              ref={inputRef}
              type='text'
              placeholder='Buscar chat...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              autoFocus
            />

            <SearchResults>
              {isLoadingSearch ? (
                <>
                  {skeleton.map((item, index) => (
                    <div style={{ padding: '10px 10px 0' }} key={`skeleton-${index}-${item.width}`}>
                      <SkeletonItem key={index}>
                        <SkeletonIcon />
                        <SkeletonText style={{ width: item.width }} />
                      </SkeletonItem>
                    </div>
                  ))}
                </>
              ) : listSearchResult.length > 0 ? (
                <AutoSizer>
                  {({ height, width }) => (
                    <InfiniteLoader
                      isItemLoaded={isItemLoaded}
                      itemCount={
                        hasMoreChats.current ? listSearchResult.length + 2 : listSearchResult.length
                      }
                      loadMoreItems={loadMoreItems}
                    >
                      {({ onItemsRendered, ref }: any) => (
                        <List
                          className='react-window-list'
                          height={height}
                          width={width}
                          itemCount={
                            hasMoreChats.current
                              ? listSearchResult.length + 2
                              : listSearchResult.length
                          }
                          itemSize={48}
                          onItemsRendered={onItemsRendered}
                          ref={(listInstance: any) => {
                            ref(listInstance)
                            listRef.current = listInstance
                          }}
                        >
                          {ItemRenderer}
                        </List>
                      )}
                    </InfiniteLoader>
                  )}
                </AutoSizer>
              ) : (
                <SearchResultNoItems>
                  <FontAwesomeIcon icon={faComment} />
                  <SearchResultItemTitle>Nenhum resultado encontrado</SearchResultItemTitle>
                </SearchResultNoItems>
              )}
            </SearchResults>
          </ModalContent>
        </ModalContainer>
      </ModalOverlay>

      <Tooltip
        anchorSelect='.llm-search-close'
        place='top'
        content='Fechar busca'
        style={{ zIndex: 10000 }}
      />
    </>
  )
}

export default memo(SearchModal)
